import Page from "components/Page";
import React, { useEffect, useState, useRef } from "react";
import { connect, ConnectedProps} from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { State } from "state";
import { createAnalyticsEvent } from "state/analytics";
import { updateNotifications } from "state/notifications";
import {addUserReportAccess, deleteProject, fetchProject, updateProject} from "state/project";
import Stepper from "components/Stepper";
import Step1Form from "components/create-project/Step1Form";
import Step2Form from "components/create-project/Step2Form";
import Step3Form from "components/create-project/Step3Form";
import Step4Form from "components/create-project/Step4Form";
import Step5Form from "components/create-project/Step5Form";
import Loader from "components/Loader";
import { createAnnouncement } from "state/announcement";

interface CreateProjectOwnProps extends RouteComponentProps<{ id: string }> { }
type CreateProjectProps = CreateProjectOwnProps & PropsFromRedux;

const CreateProjectNew: React.FC<CreateProjectProps> = (props) => {
  const {
    match: {
      params: { id: projectId },
    },
    history,
    location,
    project,
    fetchProject,
    deleteProject,
    updateProject,
    updateNotifications,
    createAnnouncement,
    addUserReportAccess
  } = props;

  const totalSteps = 5;

  const getStepFromUrl = (): number => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get("step");
    const step = stepParam ? parseInt(stepParam, 10) : 1;

    if (step >= 1 && step <= totalSteps) {
      return step;
    }
    return 1;
  };

  const [currentStep, setCurrentStep] = useState(getStepFromUrl());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchedProjectDetails, setFetchedProjectDetails] = useState({});
  const [userReportMappings, setUserReportMappings] = useState< { project_id: number | string; store_id: any; user_id: number,access:boolean }[] >([]);

  const step1SubmitRef = useRef<(() => Promise<boolean>) | null>(null);
  const step2SubmitRef = useRef<(() => Promise<boolean>) | null>(null);
  const step3SubmitRef = useRef<(() => Promise<boolean>) | null>(null);
  const step4SubmitRef = useRef<(() => Promise<boolean>) | null>(null);
  const step5SubmitRef = useRef<(() => Promise<boolean>) | null>(null);

  // const { content } = useSelector((state) => state.project.content);
  // const { content } = useSelector((state) => state.project.content);

  const updateUrlStep = (step: number) => {
    const params = new URLSearchParams(location.search);
    params.set("step", step.toString());
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  useEffect(() => {
    const stepFromUrl = getStepFromUrl();
    if (stepFromUrl !== currentStep) {
      setCurrentStep(stepFromUrl);
    }
  }, [location.search]);

  useEffect(() => {
    fetchProject(projectId);
  }, [fetchProject, projectId]);

  const handleNextStep = async () => {
    setIsSubmitting(true);
    if (currentStep === 4 && userReportMappings.length > 0) {
     await addUserReportAccess(userReportMappings);
    }
    try {
      let canProceed = true;


if (currentStep === 1) {
  if (step1SubmitRef.current) {
    const isSaved = await step1SubmitRef.current();
    if (!isSaved) {
      setIsSubmitting(false);
      return;
    }
  }
  const nextStep = currentStep + 1;
  setCurrentStep(nextStep);
  updateUrlStep(nextStep);
  return;
}

      switch (currentStep) {
        case 2:
          if (step2SubmitRef.current) {
            canProceed = await step2SubmitRef.current();
          }
          break;
        case 3:
          if (step3SubmitRef.current) {
            canProceed = await step3SubmitRef.current();
          }
          break;
        case 4:
          // Save Step1 again (important)
          if (step1SubmitRef.current) {
            await step1SubmitRef.current();
          }

          // Save Step4 mappings (already handled via parent)
          if (step4SubmitRef.current) {
            canProceed = await step4SubmitRef.current();
          }

          //  refresh project before going to Step5
          await fetchProject(projectId);

          break;
        case 5:
          if (step5SubmitRef.current) {
            canProceed = await step5SubmitRef.current();
          }
          break;
        default:
          canProceed = true;
      }

    if (canProceed && currentStep < totalSteps) {
      const nextStep = currentStep + 1;
      if (nextStep === 5) {
        await fetchProject(projectId);
      }
      setCurrentStep(nextStep);
      updateUrlStep(nextStep);
    }
    } catch (error) {
      console.error("Error during step submission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      updateUrlStep(prevStep);
    }
  };

  const handleCreate = async () => {
    setIsSubmitting(false);
    try {
      let canProceed = true;
      if (step5SubmitRef.current) {
        canProceed = await step5SubmitRef.current();
      }
      if (project && project?.automatedAnnouncement && Number(project?.published) === 1) {
        try {
          const announcement = {
            project_id: Number(projectId),
            title: project?.name + ' update.' || "",
            body: 'There is an update on' + project?.name || {},
          };

          const formData = new FormData();
          formData.append("project_id", announcement.project_id.toString());
          formData.append("title", announcement.title);
          formData.append("body", JSON.stringify(announcement.body));

          // await createAnnouncement(formData, {
          //   name: project?.name,
          //   type: project?.project_type,
          // });

          let canProceed = true;
          if (step5SubmitRef.current) {
            canProceed = await step5SubmitRef.current();
          }

          if (canProceed) {
            history.push("/manage-all-data/projects");
          }
        } catch (error) {
          console.error("Error on final step:", error);
        } finally {
          setIsSubmitting(false);
        }
      }

      if (canProceed) {
        history.push("/manage-all-data/projects");
      }
    } catch (error) {
      console.error("Error creating project:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initialize URL with step=1 if no step parameter exists
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("step")) {
      updateUrlStep(1);
    }
  }, []);

  if (!project) {
    return (
      <Loader />
    );
  }
  const isStep1Valid = (project: any): boolean => {
    if (!project) return false;

    const hasValidChain = project.chain && project.chain.id;
    const hasValidName = project.name && project.name.trim() !== "" && project.name !== "Untitled";

    return hasValidChain && hasValidName;
  };

  const handleUsersUpdate = (usersList:any)=>{
    if(usersList && usersList.length>0){
      setUserReportMappings(usersList)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1Form projectId={projectId} submitRef={step1SubmitRef} {...props} />;
      case 2:
        return (
          <Step2Form
            projectId={projectId}
            // submitRef={step2SubmitRef}
            {...props}
          />
        );
      case 3:
        return (
          <Step3Form
            projectId={projectId}
            // submitRef={step3SubmitRef}
            {...props}
          />
        );
      case 4:
        return (
          <Step4Form
            projectId={projectId}
            // submitRef={step4SubmitRef}
            onUsersChange={handleUsersUpdate}
            {...props}
          />
        );
      case 5:
        return (
          <Step5Form
            projectId={projectId}
            // submitRef={step5SubmitRef}
            {...props}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Page style={styles.container}>
      <div style={styles.subContainer}>
        <div style={styles.stepNavigation}>
          <button
            style={{
              ...styles.navButton,
              ...(currentStep === 1 && styles.navButtonDisabled),
            }}
            disabled={currentStep === 1 || isSubmitting}
            onClick={handlePreviousStep}
          >
            Previous
          </button>
          <button
            style={{
              ...styles.navButton,
              ...((isSubmitting || (currentStep === 1 && !isStep1Valid(project))) && styles.navButtonDisabled),
            }}
            disabled={isSubmitting || (currentStep === 1 && !isStep1Valid(project))}
            onClick={currentStep === totalSteps ? handleCreate : handleNextStep}
          >
            {isSubmitting ? "Processing..." : currentStep === totalSteps ? "Done" : "Next"}
          </button>
        </div>

        <Stepper currentStep={currentStep} totalSteps={totalSteps} />

        <div className="step-content">{renderStepContent()}</div>
      </div>
    </Page>
  );
};

const connector = connect(
  (state: State) => ({
    project: state.project.content,
    "Project File Error": state.STORE2POG_file_upload.hasError || false,
    "New Users Error": state.USERS_file_upload.hasError || false,
    "New Stores Error": state.STORES_file_upload.hasError || false,
    "User to Store Mapping Error": state.USER2STORE_file_upload.hasError || false,
  }),
  {
    fetchProject,
    deleteProject,
    updateProject,
    updateNotifications,
    createAnalyticsEvent,
    createAnnouncement,
    addUserReportAccess
  }
);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default withRouter(connector(CreateProjectNew));

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    height: "100%",
    width: "100%",
    backgroundColor: "#DFDFDF",
  },
  subContainer: {
    height: "100%",
    width: "100%",
    padding: "20px",
    backgroundColor: "#fff",
    borderRadius: "18px",
  },
  stepNavigation: {
    display: "flex",
    justifyContent: "space-between",
  },
  navButton: {
    fontWeight: 500,
    fontSize: "12px",
    padding: "8px 14px",
    borderRadius: "8px",
    outline: "none",
    border: "none",
    background: "#E5B42F",
    color: "#000",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  navButtonDisabled: {
    background: "#929497",
    color: "#FFFFFF",
    cursor: "not-allowed",
  },
};

// ============================================
// Usage Examples
// ============================================
/*
// Now your URLs will look like:
// http://localhost:3000/create-new-project/591?step=1
// http://localhost:3000/create-new-project/591?step=2
// http://localhost:3000/create-new-project/591?step=3

// Benefits:
// 1. Refresh keeps you on same step
// 2. Browser back/forward buttons work
// 3. Can share links to specific steps
// 4. Can bookmark any step
// 5. Clean, RESTful URL structure

// Optional: Add step validation
// If you want to prevent users from jumping to later steps without completing earlier ones,
// you can store completed steps in localStorage:

const getCompletedSteps = (): number[] => {
  const stored = localStorage.getItem('project-591-completed-steps');
  return stored ? JSON.parse(stored) : [];
};

const markStepCompleted = (step: number) => {
  const completed = getCompletedSteps();
  if (!completed.includes(step)) {
    completed.push(step);
    localStorage.setItem('project-591-completed-steps', JSON.stringify(completed));
  }
};

// In handleNextStep, after successful submission:
if (canProceed && currentStep < totalSteps) {
  markStepCompleted(currentStep);
  const nextStep = currentStep + 1;
  setCurrentStep(nextStep);
  updateUrlStep(nextStep);
}

// In getStepFromUrl validation:
const completedSteps = getCompletedSteps();
const maxAllowedStep = Math.max(...completedSteps, 0) + 1;
if (step > maxAllowedStep) {
  return maxAllowedStep;
}
*/
